import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Typography,
  TextField,
  Chip,
  Divider,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CustomButton from "../../../../components/CustomButton";
import icons from "../../../../assets/icons/icon-collection.svg";
import "./JobFilters.css";

const REGIONS = ["EMEA", "AMER", "APAC", "LATAM", "Global"];

const EMPTY_FILTERS = {
  jobTier: "",
  datePosted: "",
  jobType: [],
  experienceLevel: [],
  city: "",
  countryId: "",
  region: "",
  skills: [],
};

// ─── Section header ──────────────────────────────────────────────────────────

const SectionTitle = ({ children }) => (
  <Typography
    variant="subtitle2"
    style={{ fontWeight: 700, color: "#2c3e50", marginBottom: "10px", marginTop: "4px" }}
  >
    {children}
  </Typography>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const JobFilters = ({ open, onClose, onApply, currentFilters, countries = [] }) => {
  const [filters, setFilters] = useState(currentFilters || EMPTY_FILTERS);
  const [skillInput, setSkillInput] = useState("");

  useEffect(() => {
    setFilters(currentFilters || EMPTY_FILTERS);
    setSkillInput("");
  }, [open, currentFilters]);

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const setField = (field, value) => setFilters((prev) => ({ ...prev, [field]: value }));

  const toggleArrayValue = (field, value) => {
    setFilters((prev) => {
      const arr = prev[field] || [];
      return {
        ...prev,
        [field]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value],
      };
    });
  };

  const addSkill = (skill) => {
    const trimmed = skill.trim();
    if (!trimmed || filters.skills.includes(trimmed)) return;
    setField("skills", [...filters.skills, trimmed]);
    setSkillInput("");
  };

  const removeSkill = (skill) =>
    setField("skills", filters.skills.filter((s) => s !== skill));

  const handleSkillKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addSkill(skillInput);
    }
  };

  const handleClearAll = () => {
    setFilters(EMPTY_FILTERS);
    setSkillInput("");
  };

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  // ── Country option for Autocomplete ──────────────────────────────────────────

  const selectedCountry =
    countries.find((c) => String(c.id) === String(filters.countryId)) || null;

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="filter-dialog"
      PaperProps={{
        style: { width: "500px", maxWidth: "95vw", borderRadius: "12px", padding: "4px" },
      }}
    >
      <DialogTitle id="filter-dialog" disableTypography>
        <div className="d-flex justify-content-between align-items-center">
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            Filter Jobs
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="close">
            <svg width="24" height="24">
              <use href={`${icons}#close`} />
            </svg>
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent dividers style={{ maxHeight: "65vh", overflowY: "auto", padding: "20px 24px" }}>

        {/* ── Job Tier ─────────────────────────────────────────────────────── */}
        <SectionTitle>Job Tier</SectionTitle>
        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            row
            value={filters.jobTier}
            onChange={(e) => setField("jobTier", e.target.value)}
          >
            {[{ value: "", label: "All" }, { value: "1", label: "Tier 1" }, { value: "2", label: "Tier 2" }, { value: "3", label: "Tier 3" }].map(({ value, label }) => (
              <FormControlLabel key={value} value={value} control={<Radio color="primary" size="small" />} label={label} />
            ))}
          </RadioGroup>
        </FormControl>

        <Divider style={{ margin: "16px 0" }} />

        {/* ── Date Posted ──────────────────────────────────────────────────── */}
        <SectionTitle>Date Posted</SectionTitle>
        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            row
            value={filters.datePosted}
            onChange={(e) => setField("datePosted", e.target.value)}
          >
            {[
              { value: "", label: "Any Time" },
              { value: "today", label: "Today" },
              { value: "3days", label: "Last 3 Days" },
              { value: "7days", label: "Last 7 Days" },
              { value: "14days", label: "Last 14 Days" },
            ].map(({ value, label }) => (
              <FormControlLabel key={value} value={value} control={<Radio color="primary" size="small" />} label={label} />
            ))}
          </RadioGroup>
        </FormControl>

        <Divider style={{ margin: "16px 0" }} />

        {/* ── Job Type ─────────────────────────────────────────────────────── */}
        <SectionTitle>Job Type</SectionTitle>
        <FormGroup row>
          {[{ value: "onsite", label: "On-site" }, { value: "remote", label: "Remote" }, { value: "hybrid", label: "Hybrid" }].map(({ value, label }) => (
            <FormControlLabel
              key={value}
              control={
                <Checkbox
                  checked={filters.jobType.includes(value)}
                  onChange={() => toggleArrayValue("jobType", value)}
                  color="primary"
                  size="small"
                />
              }
              label={label}
            />
          ))}
        </FormGroup>

        <Divider style={{ margin: "16px 0" }} />

        {/* ── Experience Level ─────────────────────────────────────────────── */}
        <SectionTitle>Experience Level</SectionTitle>
        {[
          { label: "Entry Level", options: [{ value: "graduate", label: "Graduate / Intern" }, { value: "entry", label: "Entry Level (0–2 yrs)" }] },
          { label: "Mid Level", options: [{ value: "mid", label: "Mid Level (3–5 yrs)" }, { value: "senior", label: "Senior (6–10 yrs)" }] },
          { label: "Senior Level", options: [{ value: "manager", label: "Manager / Lead" }, { value: "executive", label: "Executive / Director" }] },
        ].map(({ label, options }) => (
          <div key={label} className="experience-group">
            <div className="experience-group-title">{label}</div>
            <FormGroup row>
              {options.map(({ value, label: optLabel }) => (
                <FormControlLabel
                  key={value}
                  control={
                    <Checkbox
                      checked={filters.experienceLevel.includes(value)}
                      onChange={() => toggleArrayValue("experienceLevel", value)}
                      color="primary"
                      size="small"
                    />
                  }
                  label={optLabel}
                />
              ))}
            </FormGroup>
          </div>
        ))}

        <Divider style={{ margin: "16px 0" }} />

        {/* ── Location ─────────────────────────────────────────────────────── */}
        <SectionTitle>Location</SectionTitle>
        <TextField
          label="City"
          placeholder="e.g. Lagos, London, New York"
          value={filters.city}
          onChange={(e) => setField("city", e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          style={{ marginBottom: "12px" }}
          inputProps={{ maxLength: 100 }}
        />

        <Divider style={{ margin: "16px 0" }} />

        {/* ── Target Country ───────────────────────────────────────────────── */}
        <SectionTitle>Target Country</SectionTitle>
        <Typography variant="caption" style={{ color: "#6c757d", display: "block", marginBottom: "8px" }}>
          Filter by the country a job is targeting. Older jobs without targeting will still appear.
        </Typography>
        <Autocomplete
          options={countries}
          getOptionLabel={(c) => c.name || ""}
          value={selectedCountry}
          onChange={(_, newValue) => {
            setField("countryId", newValue ? String(newValue.id) : "");
            if (newValue) setField("region", ""); // clear region when country selected
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Country"
              variant="outlined"
              size="small"
              placeholder="Search country..."
            />
          )}
          clearOnEscape
          size="small"
        />

        <Divider style={{ margin: "16px 0" }} />

        {/* ── Target Region ────────────────────────────────────────────────── */}
        <SectionTitle>Target Region</SectionTitle>
        <Typography variant="caption" style={{ color: "#6c757d", display: "block", marginBottom: "8px" }}>
          Filter by region targeting. Older jobs without region targeting will still appear.
        </Typography>
        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            row
            value={filters.region}
            onChange={(e) => {
              setField("region", e.target.value);
              if (e.target.value) setField("countryId", ""); // clear country when region selected
            }}
          >
            {[{ value: "", label: "Any" }, ...REGIONS.map((r) => ({ value: r, label: r }))].map(({ value, label }) => (
              <FormControlLabel key={value} value={value} control={<Radio color="primary" size="small" />} label={label} />
            ))}
          </RadioGroup>
        </FormControl>

        <Divider style={{ margin: "16px 0" }} />

        {/* ── Skills ───────────────────────────────────────────────────────── */}
        <SectionTitle>Skills</SectionTitle>
        <Typography variant="caption" style={{ color: "#6c757d", display: "block", marginBottom: "8px" }}>
          Type a skill and press Enter or comma to add.
        </Typography>
        <TextField
          label="Add skill"
          placeholder="e.g. Python, React, Project Management"
          value={skillInput}
          onChange={(e) => setSkillInput(e.target.value)}
          onKeyDown={handleSkillKeyDown}
          onBlur={() => skillInput.trim() && addSkill(skillInput)}
          variant="outlined"
          size="small"
          fullWidth
          style={{ marginBottom: "10px" }}
          inputProps={{ maxLength: 80 }}
        />
        {filters.skills.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            {filters.skills.map((skill) => (
              <Chip
                key={skill}
                label={skill}
                onDelete={() => removeSkill(skill)}
                color="primary"
                variant="outlined"
                size="small"
              />
            ))}
          </div>
        )}

      </DialogContent>

      <DialogActions className="d-flex justify-content-between p-3">
        <CustomButton
          text="Clear All"
          handleClick={handleClearAll}
          backgroundColor="#e3e3e3"
          color="#333333"
          additionalClass="px-4"
        />
        <div className="d-flex" style={{ gap: "12px" }}>
          <CustomButton
            text="Cancel"
            handleClick={onClose}
            backgroundColor="#6c757d"
            color="#fff"
            additionalClass="px-4"
          />
          <CustomButton
            text="Apply"
            handleClick={handleApply}
            backgroundColor="#3A6ED4"
            color="#fff"
            additionalClass="px-4"
          />
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default JobFilters;
